import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import Layout from '@/components/feature/Layout';
import Breadcrumb from '@/components/feature/Breadcrumb';
import NewsListItem from '@/components/feature/NewsListItem';
import { newsList } from '@/mocks/newsData';
import { newsTags } from '@/mocks/siteData';
import type { NewsItem, NewsTag } from '@/types';

type TagFilter = '全部' | NewsTag;
type SortKey = 'latest' | 'hot';

const tagTabs: TagFilter[] = ['全部', ...(newsTags as NewsTag[])];

export default function InvestmentNewsCategory() {
  const [tag, setTag] = useState<TagFilter>('全部');
  const [sort, setSort] = useState<SortKey>('latest');
  const [visible, setVisible] = useState(5);

  const filtered = useMemo(() => {
    let list = (newsList as NewsItem[]).filter((item) => tag === '全部' || item.tag === tag);
    list = [...list].sort((a, b) =>
      sort === 'hot' ? b.views - a.views : new Date(b.date).getTime() - new Date(a.date).getTime(),
    );
    return list;
  }, [tag, sort]);

  const shown = filtered.slice(0, visible);
  const hasMore = filtered.length > visible;

  const hotArticles = [...(newsList as NewsItem[])].sort((a, b) => b.views - a.views).slice(0, 5);

  const reset = () => {
    setTag('全部');
    setSort('latest');
    setVisible(5);
  };

  return (
    <Layout>
      <title>投资资讯 - 市场分析与投资策略 - 黄金</title>
      <meta
        name="description"
        content="投资资讯分类页汇聚黄金市场权威分析、投资策略、政策法规与热点解读，内容持续更新，助你深入理解金价运行逻辑。"
      />
      <meta name="keywords" content="投资资讯,黄金分析,黄金投资策略,贵金属资讯,黄金市场" />

      <div className="border-b border-background-200 bg-background-100">
        <div className="mx-auto max-w-portal px-4 py-6 md:px-6">
          <h1 className="font-heading text-2xl font-bold text-foreground-950 md:text-3xl">投资资讯</h1>
          <div className="mt-3">
            <Breadcrumb items={[{ label: '首页', to: '/' }, { label: '投资资讯' }]} />
          </div>
        </div>
      </div>

      <div className="mx-auto max-w-portal px-4 py-8 md:px-6 md:py-10">
        <div className="grid gap-8 lg:grid-cols-[1fr_300px]">
          <div>
            <div className="rounded-lg border border-background-200 bg-background-50 p-4 md:p-5">
              <div className="flex flex-wrap gap-2">
                {tagTabs.map((item) => (
                  <button
                    key={item}
                    type="button"
                    onClick={() => { setTag(item); setVisible(5); }}
                    className={`rounded-full px-4 py-1.5 text-sm font-medium transition-colors whitespace-nowrap cursor-pointer ${
                      tag === item
                        ? 'bg-primary-500 text-foreground-950'
                        : 'bg-background-100 text-foreground-700 hover:bg-primary-100 hover:text-primary-800'
                    }`}
                  >
                    {item}
                  </button>
                ))}
              </div>

              <div className="mt-4 flex items-center justify-between border-t border-background-200 pt-4">
                <span className="text-sm text-foreground-500">共 {filtered.length} 篇文章</span>
                <div className="flex rounded-full border border-background-300 p-1">
                  <button
                    type="button"
                    onClick={() => setSort('latest')}
                    className={`flex items-center gap-1 rounded-full px-4 py-1.5 text-sm font-medium transition-colors whitespace-nowrap cursor-pointer ${
                      sort === 'latest' ? 'bg-secondary-500 text-background-50' : 'text-foreground-600'
                    }`}
                  >
                    <i className="ri-time-line" aria-hidden="true"></i>最新
                  </button>
                  <button
                    type="button"
                    onClick={() => setSort('hot')}
                    className={`flex items-center gap-1 rounded-full px-4 py-1.5 text-sm font-medium transition-colors whitespace-nowrap cursor-pointer ${
                      sort === 'hot' ? 'bg-secondary-500 text-background-50' : 'text-foreground-600'
                    }`}
                  >
                    <i className="ri-fire-line" aria-hidden="true"></i>最热
                  </button>
                </div>
              </div>
            </div>

            {shown.length > 0 ? (
              <>
                <div className="mt-5 grid gap-4">
                  {shown.map((item) => (
                    <NewsListItem key={item.id} item={item} />
                  ))}
                </div>
                {hasMore && (
                  <div className="mt-6 text-center">
                    <button
                      type="button"
                      onClick={() => setVisible((prev) => prev + 4)}
                      className="inline-flex items-center gap-1.5 rounded-md border border-primary-300 bg-primary-50 px-6 py-2.5 text-sm font-semibold text-primary-800 transition-colors hover:bg-primary-100 whitespace-nowrap cursor-pointer"
                    >
                      加载更多
                      <i className="ri-arrow-down-line" aria-hidden="true"></i>
                    </button>
                  </div>
                )}
              </>
            ) : (
              <div className="mt-8 flex flex-col items-center justify-center rounded-lg border border-dashed border-background-300 bg-background-50 py-16 text-center">
                <span className="flex h-14 w-14 items-center justify-center rounded-full bg-background-100 text-2xl text-foreground-400">
                  <i className="ri-article-line" aria-hidden="true"></i>
                </span>
                <p className="mt-4 text-base font-medium text-foreground-800">该分类下暂无文章</p>
                <p className="mt-1 text-sm text-foreground-500">换一个标签看看，或重置筛选浏览全部资讯。</p>
                <button
                  type="button"
                  onClick={reset}
                  className="mt-5 rounded-md bg-primary-500 px-5 py-2 text-sm font-semibold text-foreground-950 transition-colors hover:bg-primary-400 whitespace-nowrap cursor-pointer"
                >
                  重置筛选
                </button>
              </div>
            )}
          </div>

          <aside className="lg:sticky lg:top-28 lg:self-start">
            <div className="rounded-lg border border-background-200 bg-background-50 p-5">
              <h2 className="flex items-center gap-2 font-heading text-base font-semibold text-foreground-950">
                <i className="ri-fire-line text-accent-600" aria-hidden="true"></i>
                热门阅读
              </h2>
              <ol className="mt-3 divide-y divide-background-200">
                {hotArticles.map((news, index) => (
                  <li key={news.id} className="py-3 first:pt-0 last:pb-0">
                    <Link to={`/detail/news/${news.id}`} className="group flex gap-3">
                      <span
                        className={`flex h-5 w-5 shrink-0 items-center justify-center rounded text-xs font-bold ${
                          index < 3 ? 'bg-primary-500 text-foreground-950' : 'bg-background-200 text-foreground-600'
                        }`}
                      >
                        {index + 1}
                      </span>
                      <span className="line-clamp-2 text-sm leading-snug text-foreground-800 transition-colors group-hover:text-primary-700">
                        {news.title}
                      </span>
                    </Link>
                  </li>
                ))}
              </ol>
            </div>
          </aside>
        </div>
      </div>
    </Layout>
  );
}